import type { TFunction } from 'i18next';

import {
  type GraphQLLetter,
  GraphQLLetterFriendlyStatus,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { ActivityType } from '../recent-activity-item/recent-activity-item.logic.ts';
import type { RecentActivityItemProps } from '../recent-activity-item/recent-activity-item.tsx';

export enum LetterOutStepsEnum {
  LetterSubmitted = 'recentActivity.letter.letterSubmitted',
  InTranslation = 'recentActivity.letter.inReviewAndTranslation',
  SentToCenter = 'recentActivity.letter.sentToCenter',
}

export type RecentActivityItemLetterOutProps = {
  letter: GraphQLLetter;
};

const steps = Object.values(LetterOutStepsEnum);

export const processLetterOut = (
  letter: GraphQLLetter,
  t: TFunction,
): RecentActivityItemProps => {
  let activeStep: LetterOutStepsEnum | undefined;
  let successHeading: string | undefined;
  let errorHeading: string | undefined;
  let message: string | undefined;
  let estimatedDeliveryDate: string | undefined;
  let estimatedDeliveryTooltip: string | undefined;

  switch (letter.status) {
    case GraphQLLetterFriendlyStatus.Complete: {
      activeStep = LetterOutStepsEnum.SentToCenter;
      successHeading = t('my:recentActivity.letter.deliveredMessage');
      message = t('my:recentActivity.letter.message');

      break;
    }
    case GraphQLLetterFriendlyStatus.InTranslation:
    case GraphQLLetterFriendlyStatus.TranslationComplete:
    case GraphQLLetterFriendlyStatus.InQualityAssurance: {
      activeStep = LetterOutStepsEnum.InTranslation;
      estimatedDeliveryDate = t('my:recentActivity.onItsWay');
      estimatedDeliveryTooltip = t('my:recentActivity.letter.tooltip');
      break;
    }
    case GraphQLLetterFriendlyStatus.Submitted:
    case GraphQLLetterFriendlyStatus.InProcess:
    case GraphQLLetterFriendlyStatus.JustReceived: {
      activeStep = LetterOutStepsEnum.LetterSubmitted;
      estimatedDeliveryDate = t('my:recentActivity.onItsWay');
      estimatedDeliveryTooltip = t('my:recentActivity.letter.tooltip');
      break;
    }
    case GraphQLLetterFriendlyStatus.ReadyForDelivery: {
      activeStep = LetterOutStepsEnum.SentToCenter;
      estimatedDeliveryDate = t('my:recentActivity.onItsWay');
      estimatedDeliveryTooltip = t('my:recentActivity.letter.tooltip');
      break;
    }
    default: {
      errorHeading = t('my:recentActivity.letter.errorOut');
      message = t('my:recentActivity.contactSupport');
      break;
    }
  }

  const cardTitle = t('my:recentActivity.cardTitleS2B', {
    type: 'Letter',
    name: letter.beneficiary?.name ?? '',
  });

  const activeStepIndex = activeStep ? steps.indexOf(activeStep) : -1;

  return {
    title: cardTitle,
    direction: ActivityType.Outgoing,
    steps: steps.map((step) => t(`my:${step}`)),
    activeStepIndex: activeStepIndex,
    estimatedDelivery: estimatedDeliveryDate,
    estimatedDeliveryTooltip: estimatedDeliveryTooltip,
    message: message,
    successHeading: successHeading,
    errorHeading: errorHeading,
  };
};
